import { useEffect, useRef } from 'react';

/**
 * Returns a ref whose `.current` is `true` while the component is mounted and `false`
 * after it unmounts. Read `isMounted.current` before a post-await `setState` to avoid
 * "state update on an unmounted component" when an async task resolves after teardown.
 *
 * This is the pure, side-effect-free form of the mount/unmount guard. A hook that also
 * needs to run other cleanup on unmount (e.g. revoking an object URL) should keep its
 * own `useEffect` rather than use this.
 */
export const useIsMounted = (): React.MutableRefObject<boolean> => {
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
};
